import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  CheckCircle, FileText, Loader2, AlertTriangle, Shield, ChevronDown, ChevronUp, Lock
} from 'lucide-react';

const getApiBase = () => {
  const hostname = window.location.hostname;
  if (hostname === 'compliance.cyberforte.com.au' || hostname === 'www.compliance.cyberforte.com.au') {
    return '/api';
  }
  return `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
};

export default function PolicyAcknowledgeBundlePage() {
  const { token } = useParams();
  const API = getApiBase();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [resultMsg, setResultMsg] = useState('');
  const [openPolicy, setOpenPolicy] = useState(null); // policy_id of expanded item
  const [policyContents, setPolicyContents] = useState({}); // policy_id -> content text
  const [reviewedIds, setReviewedIds] = useState(new Set());

  // Access-code gate (6-digit code the admin shares alongside the link).
  const [codeRequired, setCodeRequired] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(null);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [verifiedCode, setVerifiedCode] = useState(null);

  const loadBundle = (code) => {
    const query = code ? `?code=${encodeURIComponent(code)}` : '';
    return axios.get(`${API}/policy-ack/public/bundle/${token}${query}`)
      .then(res => {
        if (res.data.code_required) {
          setCodeRequired(true);
          setBundle(null);
          if (res.data.invalid_code) setCodeError('Incorrect access code. Please try again.');
          return null;
        }
        setBundle(res.data);
        setCodeRequired(false);
        setCodeError(null);
        if (code) setVerifiedCode(code);
        return res.data;
      });
  };

  useEffect(() => {
    loadBundle(null)
      .catch((err) => {
        const status = err.response?.status;
        if (status === 404) setError('This link is invalid or has expired. Please contact your administrator for a new link.');
        else setError(err.response?.data?.detail || 'Unable to load policies. Please check your connection and try again.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const trimmed = codeInput.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setCodeError('Please enter the 6-digit code from your administrator.');
      return;
    }
    setVerifyingCode(true);
    setCodeError(null);
    try {
      await loadBundle(trimmed);
    } catch (err) {
      setCodeError(err.response?.data?.detail || 'Unable to verify code. Please try again.');
    } finally {
      setVerifyingCode(false);
    }
  };

  const codeQuery = verifiedCode ? `?code=${encodeURIComponent(verifiedCode)}` : '';

  const togglePolicy = async (policyId) => {
    if (openPolicy === policyId) {
      setOpenPolicy(null);
      return;
    }
    setOpenPolicy(policyId);
    setReviewedIds(prev => new Set(prev).add(policyId));
    const policy = bundle?.policies?.find(p => p.policy_id === policyId);
    if (policy && !policy.has_pdf && !policyContents[policyId]) {
      try {
        const r = await axios.get(`${API}/policy-ack/public/bundle/${token}/policy/${policyId}/content${codeQuery}`);
        setPolicyContents(prev => ({ ...prev, [policyId]: r.data.content || '(No content available)' }));
      } catch {
        setPolicyContents(prev => ({ ...prev, [policyId]: '(Unable to load content)' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/policy-ack/public/bundle/${token}/acknowledge`, {
        name, email, code: verifiedCode || undefined,
      });
      setResultMsg(res.data.message);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit acknowledgement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error && !bundle) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link Not Found</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (codeRequired) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8">
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">Access code required</h1>
            <p className="text-sm text-slate-600 mb-6">
              This policy bundle is protected. Enter the 6-digit code your administrator shared with you to continue.
            </p>
          </div>
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <Input
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              autoFocus
              className="text-center text-2xl font-mono tracking-[0.4em] h-14"
              data-testid="bundle-code-input"
            />
            {codeError && (
              <p className="text-sm text-red-600 text-center" data-testid="bundle-code-error">{codeError}</p>
            )}
            <Button
              type="submit"
              disabled={verifyingCode || codeInput.length !== 6}
              className="w-full bg-slate-900 text-white hover:bg-slate-800"
              data-testid="bundle-code-submit"
            >
              {verifyingCode ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</> : 'Continue'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">All Policies Acknowledged</h1>
          <p className="text-slate-600">{resultMsg}</p>
          <p className="text-sm text-slate-500 mt-4">You may close this page.</p>
        </div>
      </div>
    );
  }

  const allReviewed = (bundle?.policies || []).every(p => reviewedIds.has(p.policy_id));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Shield className="w-6 h-6 text-cyan-400" />
          <div>
            <h1 className="text-lg font-bold">{bundle.organization || 'Organization'}</h1>
            <p className="text-slate-400 text-sm">Policy Acknowledgement Bundle</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Bundle Info */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-2xl font-bold text-slate-900" data-testid="bundle-name">{bundle.name}</h2>
          <p className="text-slate-600 mt-2">
            Please review and acknowledge the <strong>{bundle.total_policies} policies</strong> below.
            Click each to expand and review the content.
          </p>
        </div>

        {/* Policies List */}
        <div className="space-y-3" data-testid="bundle-policies-list">
          {bundle.policies.map((p, idx) => {
            const isOpen = openPolicy === p.policy_id;
            const isReviewed = reviewedIds.has(p.policy_id);
            return (
              <div key={p.policy_id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <button
                  onClick={() => togglePolicy(p.policy_id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`bundle-policy-${p.policy_code || p.policy_id}`}
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 text-slate-700 font-semibold flex items-center justify-center text-sm">
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-semibold text-slate-900">{p.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {p.policy_code ? `${p.policy_code} · ` : ''}Version {p.version}
                        {p.has_pdf ? ' · PDF' : (p.has_content ? ' · Text' : '')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isReviewed && (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    )}
                    {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-slate-200">
                    {p.has_pdf ? (
                      <div className="bg-slate-100">
                        <div className="bg-slate-100 px-4 py-2 border-b flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="text-sm font-medium text-slate-700">{p.pdf_filename}</span>
                        </div>
                        <iframe
                          src={`${API}/policy-ack/public/bundle/${token}/policy/${p.policy_id}/pdf${codeQuery}`}
                          className="w-full border-0"
                          style={{ height: '500px' }}
                          title={p.title}
                        />
                      </div>
                    ) : (
                      <div className="p-5 max-h-[500px] overflow-auto bg-slate-50">
                        <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans leading-relaxed">
                          {policyContents[p.policy_id] || 'Loading...'}
                        </pre>
                      </div>
                    )}
                    {p.description && (
                      <p className="text-sm text-slate-600 px-4 py-3 border-t border-slate-200">{p.description}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Acknowledgement Form */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Acknowledge All Policies</h3>
          {!allReviewed && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Please open and review each policy above before acknowledging.</span>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700" data-testid="bundle-ack-error">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Full Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your full name" required data-testid="bundle-ack-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Email Address</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" required data-testid="bundle-ack-email" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer pt-2">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300"
                data-testid="bundle-ack-checkbox"
              />
              <span className="text-sm text-slate-700">
                I confirm that I have read, understood, and agree to comply with all <strong>{bundle.total_policies} policies</strong> listed above.
              </span>
            </label>
            <Button
              type="submit"
              disabled={!agreed || !name || !email || submitting || !allReviewed}
              className="w-full bg-slate-900 text-white hover:bg-slate-800"
              data-testid="bundle-ack-submit"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : `Acknowledge ${bundle.total_policies} Policies`}
            </Button>
          </form>
        </div>
      </main>

      <footer className="text-center py-6 text-xs text-slate-400">
        Cyber Forte Compliance Manager
      </footer>
    </div>
  );
}
